<template>
  <b-modal
    id="manage-phones-modal"
    :title="readonly ? 'Telefones' : 'Gerenciar Telefones'"
    size="md"
    hide-footer
    @hidden="onModalHidden"
    @shown="onModalShown"
  >
    <template v-if="isLoading">
      <div class="loading-container">
        <b-spinner label="Carregando..."></b-spinner>
      </div>
    </template>
    <template v-else>
      <div v-if="!hasPhones && readonly" class="no-phones-message">
        <p>Nenhum telefone cadastrado</p>
        <div class="modal-footer-actions">
          <button
            class="btn-cancel"
            disabled
            style="cursor: not-allowed; opacity: 0.6"
          >
            Fechar
          </button>
        </div>
      </div>
      <div v-else class="phones-manager">
        <div
          v-for="(phone, index) in phones"
          :key="`phone-${modalKey}-${index}-${phone.id || 'new'}`"
          class="phone-item"
        >
          <div class="phone-row">
            <input
              :key="`input-${modalKey}-${index}`"
              type="text"
              :name="`phone_modal_${index}`"
              v-mask="['(##) #####-####', '(##) ####-####']"
              v-model="phones[index].number"
              placeholder="Digite o telefone"
              class="phone-input form-control"
              :disabled="readonly"
            />

            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="phones[index].is_mobile"
                :disabled="readonly"
              />
              Celular
            </label>

            <button
              v-if="canRemovePhone && !readonly"
              class="btn-remove"
              @click="removePhone(index, phone.id)"
              title="Remover telefone"
            >
              <font-awesome-icon icon="fa-regular fa-trash-can" />
            </button>
          </div>
        </div>

        <button v-if="!readonly" class="btn-add" @click="addPhone">
          + Adicionar telefone
        </button>

        <div class="modal-footer-actions">
          <button
            v-if="readonly"
            class="btn-cancel"
            @click="$bvModal.hide('manage-phones-modal')"
          >
            Fechar
          </button>
          <template v-else>
            <button
              class="btn-cancel"
              @click="$bvModal.hide('manage-phones-modal')"
            >
              Cancelar
            </button>
            <button class="btn-save" @click="savePhones">Salvar</button>
          </template>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import httpRda from "../../http-rda";

library.add(faTrashCan);

export default {
  name: "ManagePhonesModal",

  components: {
    FontAwesomeIcon,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
    currentUserRole: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      phones: [],
      modalKey: 0,
      isLoading: true,
    };
  },

  computed: {
    canRemovePhone() {
      if (this.currentUserRole && this.currentUserRole.includes("parent")) {
        return this.phones.length > 1;
      }
      return this.phones.length > 1;
    },
    hasPhones() {
      return this.phones.some(
        (phone) => phone.number && phone.number.trim() !== ""
      );
    },
  },

  methods: {
    initializePhones() {
      if (this.user.Phones && Array.isArray(this.user.Phones)) {
        this.phones = JSON.parse(JSON.stringify(this.user.Phones));
      } else {
        this.phones = [];
      }

      if (this.phones.length === 0 && !this.readonly) {
        this.phones.push({ number: "", is_mobile: true });
      }
    },

    addPhone() {
      this.phones.push({ number: "", is_mobile: true });
    },

    onModalHidden() {
      this.phones = [];
      this.isLoading = true;
      this.modalKey++;
      this.$emit("modal-hidden");
    },

    async onModalShown() {
      this.isLoading = true;
      this.modalKey++;
      await this.$nextTick();
      this.initializePhones();
      setTimeout(() => {
        this.isLoading = false;
      }, 200);
    },

    async removePhone(index, phoneId) {
      if (this.currentUserRole && this.currentUserRole.includes("parent")) {
        if (this.phones.length <= 1) {
          this.$swal.fire({
            icon: "warning",
            title: "Atenção",
            text: "Você deve ter pelo menos um telefone cadastrado.",
          });
          return;
        }
      }

      if (phoneId) {
        const result = await this.$swal.fire({
          icon: "warning",
          title: "Confirmar remoção",
          text: "Tem certeza que deseja remover este telefone?",
          showCancelButton: true,
          confirmButtonText: "Sim, remover",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#d33",
          cancelButtonColor: "#6c757d",
        });

        if (!result.isConfirmed) {
          return;
        }

        try {
          await httpRda.delete(`/users/${this.userId}/phones/${phoneId}`);

          this.$swal.fire({
            toast: true,
            position: "bottom-end",
            icon: "success",
            title: "Telefone removido com sucesso!",
            showConfirmButton: false,
            timer: 3000,
          });
        } catch (error) {
          this.$swal.fire({
            icon: "error",
            title: "Erro ao remover telefone",
            text: error.message,
          });
          return;
        }
      }

      this.phones.splice(index, 1);

      this.$emit("phones-updated");
    },

    async savePhones() {
      try {
        const validPhones = this.phones.filter(
          (phone) => phone.number && phone.number.trim() !== ""
        );

        if (validPhones.length === 0) {
          this.$swal.fire({
            icon: "warning",
            title: "Atenção",
            text: "Você deve ter pelo menos um telefone válido.",
          });
          return;
        }

        const phonesToCreate = validPhones.filter((phone) => !phone.id);
        const phonesToUpdate = validPhones.filter((phone) => phone.id);

        for (const phone of phonesToCreate) {
          await httpRda.post(`/users/${this.userId}/phones`, {
            number: phone.number,
            is_mobile: phone.is_mobile !== undefined ? phone.is_mobile : true,
          });
        }

        for (const phone of phonesToUpdate) {
          await httpRda.patch(`/users/${this.userId}/phones/${phone.id}`, {
            number: phone.number,
            is_mobile: phone.is_mobile !== undefined ? phone.is_mobile : true,
          });
        }

        this.$swal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Telefones salvos com sucesso!",
          showConfirmButton: false,
          timer: 3000,
        });

        this.$emit("phones-updated");

        this.$bvModal.hide("manage-phones-modal");
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: "Erro ao salvar telefones",
          text: error.message,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;
  padding: 40px 0;
}

.no-phones-message {
  padding: 20px 0;
  text-align: center;

  p {
    color: #6c757d;
    font-size: 15px;
    margin-bottom: 24px;
  }

  .modal-footer-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 0;

    .btn-cancel {
      background: white;
      border: 1px solid #ced4da;
      border-radius: 6px;
      padding: 8px 20px;
      font-size: 14px;
      cursor: pointer;

      &:hover {
        background: #f8f9fa;
      }

      &:disabled,
      &[disabled] {
        cursor: not-allowed !important;
        opacity: 0.6;
        background: white;
        border: 1px solid #ced4da;
      }

      &:disabled:hover,
      &[disabled]:hover {
        background: white;
      }
    }
  }
}

.phones-manager {
  padding: 8px 0;

  .phone-item {
    border-bottom: 1px solid #e6e6e6;
    padding-bottom: 12px;
    margin-bottom: 12px;

    &:last-of-type {
      border-bottom: none;
    }
  }

  .phone-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .phone-input {
    flex: 1;
    height: 42px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 0 12px;
    font-size: 15px;
    outline: none;

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.15);
    }

    &:disabled {
      background-color: #e9ecef;
      cursor: not-allowed;
    }
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-size: 14px;
    margin: 0;
    cursor: pointer;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
      }
    }
  }

  .btn-remove {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }

  .btn-add {
    margin-top: 4px;
    background: #68c5b7;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #ced4da;
      color: white;
      border: 1px solid #ced4da;
    }
  }

  .modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 24px;

    .btn-cancel {
      background: white;
      border: 1px solid #ced4da;
      border-radius: 6px;
      padding: 8px 20px;
      font-size: 14px;
      cursor: pointer;

      &:hover {
        background: #f8f9fa;
      }
    }

    .btn-save {
      background: #68c5b7;
      color: white;
      border: none;
      border-radius: 6px;
      padding: 8px 20px;
      font-size: 14px;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: #ced4da;
        color: white;
        border: 1px solid #ced4da;
      }
    }
  }
}
</style>
